import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthStatus, AuthStore } from "../../../stores/AuthStore";
import { useStore } from "../../../utils/useStore";
import AppLoadingScreen from "../AppLoadingScreen";
import {
    ApplicationPaths,
    LoginActions,
    QueryParameterNames,
} from "./ApiAuthorizationConstants";
import authService, { AuthenticationResultStatus } from "./AuthorizeService";

// The main responsibility of this component is to handle the user's login process.
// This is the starting point for the login process. Any component that needs to
// authenticate a user can simply navigate to this component with a returnUrl query
// parameter and let the component perform the login and return back to the return url.
//
// Sign-in is silent-only (hidden iframe): it succeeds whenever an
// IdentityServer session cookie exists. When it fails the user is sent to
// the interactive login page. All navigation is client-side so no full
// document reloads (white flashes) happen anywhere in this flow.
export function Login({ action }) {
    const navigate = useNavigate();
    const authStore = useStore(AuthStore);
    const [message, setMessage] = useState(undefined);
    const startedRef = useRef(false);

    useEffect(() => {
        if (startedRef.current) {
            return;
        }
        startedRef.current = true;

        const getReturnUrl = (state) => {
            const params = new URLSearchParams(window.location.search);
            const fromQuery = params.get(QueryParameterNames.ReturnUrl);
            return (
                (state && state.returnUrl) ||
                fromQuery ||
                `${window.location.origin}/`
            );
        };

        // Convert a return url into a same-origin path the router can
        // navigate to. Anything cross-origin or unparsable falls back to
        // the app root instead of being followed (also closes the open
        // redirect the previous location.replace-based flow allowed).
        const toSpaPath = (url) => {
            try {
                const resolved = new URL(url || "/app", window.location.origin);
                if (resolved.origin !== window.location.origin) {
                    return "/app";
                }
                return `${resolved.pathname}${resolved.search}${resolved.hash}`;
            } catch {
                return "/app";
            }
        };

        // AuthorizeService only stores the oidc user; the AuthStore still
        // needs to mark itself authenticated and load its dependent stores
        // before AuthorizeRouteV6 will render the app.
        const ensureAuthStorePopulated = async () => {
            if (
                !authStore._isAuthenticated ||
                authStore.status !== AuthStatus.Initialized
            ) {
                await authStore.populateAuth();
            }
        };

        async function run() {
            switch (action) {
                case LoginActions.Login: {
                    const returnUrl = getReturnUrl();
                    const result = await authService.signIn({ returnUrl });
                    if (
                        result.status === AuthenticationResultStatus.Success
                    ) {
                        await ensureAuthStorePopulated();
                        navigate(toSpaPath(returnUrl), { replace: true });
                    } else {
                        // No IdentityServer session: interactive login,
                        // reached without leaving the SPA.
                        navigate(
                            `/auth/LoginUser?ReturnUrl=${encodeURIComponent(
                                toSpaPath(returnUrl),
                            )}`,
                            { replace: true },
                        );
                    }
                    break;
                }
                case LoginActions.LoginCallback: {
                    const result = await authService.completeSignIn(
                        window.location.href,
                    );
                    if (
                        result.status === AuthenticationResultStatus.Success
                    ) {
                        await ensureAuthStorePopulated();
                        navigate(toSpaPath(getReturnUrl(result.state)), {
                            replace: true,
                        });
                    } else if (
                        result.status === AuthenticationResultStatus.Fail &&
                        result.message?.includes?.(
                            "No matching state found in storage",
                        )
                    ) {
                        navigate("/app", { replace: true });
                    } else {
                        setMessage(result.message);
                    }
                    break;
                }
                case LoginActions.LoginFailed: {
                    const params = new URLSearchParams(window.location.search);
                    setMessage(params.get(QueryParameterNames.Message));
                    break;
                }
                case LoginActions.Profile:
                    window.location.replace(
                        `${window.location.origin}${ApplicationPaths.IdentityManagePath}`,
                    );
                    break;
                case LoginActions.Register:
                    window.location.replace(
                        `${window.location.origin}${
                            ApplicationPaths.IdentityRegisterPath
                        }?${QueryParameterNames.ReturnUrl}=${encodeURI(
                            ApplicationPaths.Login,
                        )}`,
                    );
                    break;
                default:
                    throw new Error(`Invalid action '${action}'`);
            }
        }

        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (message) {
        return <div>{message}</div>;
    }

    return <AppLoadingScreen />;
}
